import { formatDate } from "@angular/common";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { GenericResponse } from "../domain/genericResponse";
import { VehiculoDTO } from "../domain/vehiculo-dto";
import { GLOBAL } from "../global";

const httpOptions = {
  headers: new HttpHeaders({ "Content-Type": "application/json" })
};

@Injectable({
  providedIn: "root"
})
export class VehiculoService {

  urlVehiculo: string;
  
  /**
   * @author Andrés Felipe Vargas López
   * @version Nov 04, 2018
   * @since 6.2.4
   * @param httpClient  - Cliente http que realiza las peticiones
   */
  constructor(private httpClient: HttpClient) {
    this.urlVehiculo = GLOBAL.URL_BASE + "sicaVehiculo";
  }
  
  /**
   * Servicio `REST` de tipo `POST` que consulta los vehiculos de un cliente.
   * @author Andrés Felipe Vargas López
   * @version Nov 04, 2018
   * @since 6.2.4
   * @param parametros - DTO en el que se incluyen todos los parametros para consumir el servicio rest.
   * @return [ `Observable<GenericResponse>` ] - Se retorna la repuesta enviada por la peticion http.
   */
  public consultaVehiculosPorTercero (parametros: VehiculoDTO): Observable<GenericResponse> {
    const operation: string = "findAllVehiculosActivosByTercId";
    const url:  string = `${this.urlVehiculo}/${operation}`;

    return this.httpClient.post<GenericResponse>(url, parametros, httpOptions)
    .pipe(
      tap(_ => this.log(`Petición realizada con éxito hacia : /${operation} [POST]`, "INFO")),
      catchError(this.handleServiceError<GenericResponse>(
        "POST", 
        operation,
        new GenericResponse(GLOBAL.HTTP_NOT_FOUND, "ERROR 404 NOT FOUND", [])
      ))
    );
  }

  /**
   * Servicio `REST` de tipo `POST` que solicita la asignacion de un vin a un tercero.
   * @author Andrés Felipe Vargas López
   * @version Nov 09, 2018
   * @since 6.2.4
   * @param parametros - DTO en el que se incluyen todos los parametros para consumir el servicio rest.
   * @return [ `Observable<GenericResponse>` ] - Se retorna la repuesta enviada por la peticion http.
   */
  public solicitarAsignacionVinATercero (parametros: VehiculoDTO): Observable<GenericResponse> {
    const operation: string = "solicitarAsignacionVinATercero";
    const url:  string = `${this.urlVehiculo}/${operation}`;

    return this.httpClient.post<GenericResponse>(url, parametros, httpOptions)
    .pipe(
      tap(_ => this.log(`Petición realizada con éxito hacia : /${operation} [POST]`, "INFO")),
      catchError(this.handleServiceError<GenericResponse>(
        "POST", 
        operation,
        new GenericResponse(GLOBAL.HTTP_NOT_FOUND, "ERROR 404 NOT FOUND", [])
      ))
    );
  }

  /**
   * Servicio `REST` de tipo `POST` que solicita la eliminacion de un vin a un tercero.
   * @author Andrés Felipe Vargas López
   * @version Nov 09, 2018
   * @since 6.2.4
   * @param parametros - DTO en el que se incluyen todos los parametros para consumir el servicio rest.
   * @return [ `Observable<GenericResponse>` ] - Se retorna la repuesta enviada por la peticion http.
   */
  public solicitarEliminacionVinATercero (parametros: VehiculoDTO): Observable<GenericResponse> {
    const operation: string = "solicitarEliminacionVinATercero";
    const url:  string = `${this.urlVehiculo}/${operation}`;

    return this.httpClient.post<GenericResponse>(url, parametros, httpOptions)
    .pipe(
      tap(_ => this.log(`Petición realizada con éxito hacia : /${operation} [POST]`, "INFO")),
      catchError(this.handleServiceError<GenericResponse>(
        "POST", 
        operation,
        new GenericResponse(GLOBAL.HTTP_NOT_FOUND, "ERROR 404 NOT FOUND", [])
      ))
    );
  }

  /**
   * Servicio `REST` de tipo `POST` que actualiza el kilometraje y la proxima fecha de 
   * cambio de aceite de un vehiculo.
   * @author Andrés Felipe Vargas López
   * @version Nov 09, 2018
   * @since 6.2.4
   * @param parametros - DTO en el que se incluyen todos los parametros para consumir el servicio rest.
   * @return [ `Observable<GenericResponse>` ] - Se retorna la repuesta enviada por la peticion http.
   */
  public actualizarDatosVehiculos (parametros: VehiculoDTO): Observable<GenericResponse> {
    const operation: string = "actualizarDatosVehiculos";
    const url:  string = `${this.urlVehiculo}/${operation}`;

    return this.httpClient.post<GenericResponse>(url, parametros, httpOptions)
    .pipe(
      tap(_ => this.log(`Petición realizada con éxito hacia : /${operation} [POST]`, "INFO")),
      catchError(this.handleServiceError<GenericResponse>(
        "POST", 
        operation,
        new GenericResponse(GLOBAL.HTTP_NOT_FOUND, "ERROR 404 NOT FOUND", [])
      ))
    );
  }


  /**
   * Servicio `REST` de tipo `POST` que procesa los vehiculos de un usuario cada vez que este
   * inicie sesion
   * @author Andrés Felipe Vargas López
   * @version Nov 19, 2018
   * @since 6.2.4
   * @param parametros - DTO en el que se incluyen todos los parametros para consumir el servicio rest, para este caso el terc_id.
   * @return [ `Observable<GenericResponse>` ] - Se retorna la repuesta enviada por la peticion http.
   */
  public procesarVehiculosTercero (parametros: VehiculoDTO): Observable<GenericResponse> {
    const operation: string = "procesarVehiculosTercero";
    const url:  string = `${this.urlVehiculo}/${operation}`;

    return this.httpClient.post<GenericResponse>(url, parametros, httpOptions)
    .pipe(
      tap(_ => this.log(`Petición realizada con éxito hacia : /${operation} [POST]`, "INFO")),
      catchError(this.handleServiceError<GenericResponse>(
        "POST", 
        operation,
        new GenericResponse(GLOBAL.HTTP_NOT_FOUND, "ERROR 404 NOT FOUND", [])
      ))
    );
  }


  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @author Andrés Felipe Vargas López
   * @version Nov 04, 2018
   * @since 6.2.4
   * @param operation - name of the operation that failed.
   * @param result - optional value to return as the observable result.
   * @return [ Observable<T> ] - Returns service response catched before error.
   *
   */
  private handleServiceError<T> (method: string, operation = "operation", result?: T) {
    return (error: any): Observable<T> => {

      this.log(`${method} rest service: /${operation} failed: ${error.message}`, "ERROR");

      // console.error(error);// log to console instead
      
      // Let the app keep running by returning an empty result.
      return of(result as T);

    };
  }

  /**
   * Log a message in console
   * @author Andrés Felipe Vargas López
   * @version Nov 04, 2018
   * @since 6.2.4
   * @param message - Message to log.
   * @param type - Log type (ERROR, INFO, WARN, DEBUG)
   */
  private log(message: string, type: string) {
    
    let now: string = formatDate(new Date(), "dd-MM-yyyyTHH:mm:ss,SSS", "es-CO");
    message = now + " " + type +" [src/app/service/vehiculo.service.ts] - " + message;

    switch(type) { 
      case "INFO": { 
         //console.info(message); 
         break; 
      } 
      case "WARN": { 
         //console.warn(message); 
         break; 
      } 
      case "ERROR": {
         //console.error(message); 
         break;    
      } 
      case "DEBUG": { 
         //console.debug(message); 
         break; 
      }  
      default: { 
         //console.log(message); 
         break;              
      } 
    }

  }

}
